/* eslint-disable no-nested-ternary */
import React from 'react';
import PropTypes from 'prop-types';
import {
    Box,
    Typography,
    TableContainer,
    Table,
    TableHead,
    TableBody,
    TableRow,
    TableCell,
    alpha,
    useMediaQuery,
    useTheme,
    Card,
    CardContent,
    Stack,
    Divider
} from '@mui/material';
import { theme } from '../../ConstantColors';

const AcquittalLineActivitiesTable = ({
    lineActivities = [],
    title = 'Acquiattal Line Activities',
    formatCurrency = (amount) => `$${Number(amount).toFixed(2)}`,
    acquittalData = null
}) => {
    const themeContext = useTheme();
    const isMobile = useMediaQuery(themeContext.breakpoints.down('sm'));

    const calculateLineActivityTotals = (activities) =>
        activities.reduce(
            (totals, activity) => {
                const meals = (activity.breakfast || 0) + (activity.lunch || 0) + (activity.dinner || 0);
                const accommodation = activity.accommodation || 0;
                const additional = (activity.incidental || 0) + (activity.miscellaneous || 0);

                return {
                    meals: totals.meals + meals,
                    accommodation: totals.accommodation + accommodation,
                    additional: totals.additional + additional,
                    total: totals.total + meals + accommodation + additional
                };
            },
            { meals: 0, accommodation: 0, additional: 0, total: 0 }
        );

    return (
        <Box sx={{ mt: 4 }}>
            <Typography
                variant="h6"
                sx={{
                    color: theme.primary.main,
                    display: 'flex',
                    alignItems: 'center',
                    mb: 3,
                    fontWeight: 600
                }}
            >
                {title}
            </Typography>

            {isMobile ? (
                <Stack spacing={2}>
                    {lineActivities.map((activity) => {
                        const meals = (activity.breakfast || 0) + (activity.lunch || 0) + (activity.dinner || 0);
                        const additional = (activity.incidental || 0) + (activity.miscellaneous || 0);
                        const rowTotal = meals + (activity.accommodation || 0) + additional;

                        return (
                            <Card key={activity.id || activity.activityDate} variant="outlined" sx={{ borderRadius: 2 }}>
                                <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                                    <Stack spacing={1.5}>
                                        <Typography variant="subtitle1" sx={{ fontWeight: 600, color: theme.primary.main }}>
                                            {new Date(activity.activityDate).toLocaleDateString()}
                                        </Typography>
                                        <Divider />
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                            <Typography variant="body2">Meals:</Typography>
                                            <Typography variant="subtitle2">{formatCurrency(meals)}</Typography>
                                        </Box>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                            <Typography variant="body2">Accommodation:</Typography>
                                            <Typography variant="subtitle2">{formatCurrency(activity.accommodation || 0)}</Typography>
                                        </Box>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                            <Typography variant="body2">Additional:</Typography>
                                            <Typography variant="subtitle2">{formatCurrency(additional)}</Typography>
                                        </Box>
                                        <Divider />
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                                                Total:
                                            </Typography>
                                            <Typography variant="h5" sx={{ fontWeight: 700, color: theme.primary.main }}>
                                                {formatCurrency(rowTotal)}
                                            </Typography>
                                        </Box>
                                    </Stack>
                                </CardContent>
                            </Card>
                        );
                    })}

                    {lineActivities.length > 0 && (
                        <Card sx={{ bgcolor: theme.primary.lighter, borderRadius: 2, border: `1px solid ${theme.primary.main}` }}>
                            <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                                <Typography variant="subtitle1" sx={{ fontWeight: 700, color: theme.primary.dark, mb: 2 }}>
                                    SUMMARY
                                </Typography>
                                <Stack spacing={1}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                        <Typography variant="body2">TOTAL Meals:</Typography>
                                        <Typography variant="subtitle2">
                                            {formatCurrency(calculateLineActivityTotals(lineActivities).meals)}
                                        </Typography>
                                    </Box>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                        <Typography variant="body2">TOTAL Accommodation:</Typography>
                                        <Typography variant="subtitle2">
                                            {formatCurrency(calculateLineActivityTotals(lineActivities).accommodation)}
                                        </Typography>
                                    </Box>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                        <Typography variant="body2">TOTAL Additional:</Typography>
                                        <Typography variant="subtitle2">
                                            {formatCurrency(calculateLineActivityTotals(lineActivities).additional)}
                                        </Typography>
                                    </Box>
                                    <Divider sx={{ my: 1, borderColor: theme.primary.main }} />
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <Typography variant="subtitle1" sx={{ fontWeight: 700, color: theme.primary.dark }}>
                                            TOTAL:
                                        </Typography>
                                        <Typography variant="h4" sx={{ fontWeight: 800, color: theme.primary.dark }}>
                                            {formatCurrency(calculateLineActivityTotals(lineActivities).total)}
                                        </Typography>
                                    </Box>

                                    {acquittalData && (
                                        <>
                                            <Divider sx={{ my: 1 }} />
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                                <Typography variant="subtitle2" sx={{ color: '#2E7D32' }}>
                                                    TOTAL SPENT:
                                                </Typography>
                                                <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#2E7D32' }}>
                                                    {formatCurrency(acquittalData.totalSpentAmount)}
                                                </Typography>
                                            </Box>
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                                <Typography variant="subtitle2" sx={{ color: '#2E7D32' }}>
                                                    TOTAL RECEIVED:
                                                </Typography>
                                                <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#2E7D32' }}>
                                                    {formatCurrency(acquittalData.clientReceivedAmount)}
                                                </Typography>
                                            </Box>
                                            <Divider sx={{ my: 1 }} />
                                            <Box
                                                sx={{
                                                    p: 1.5,
                                                    borderRadius: 1,
                                                    bgcolor: (theme) =>
                                                        acquittalData.varianceAmount === 0
                                                            ? alpha(theme.palette.success.main, 0.1)
                                                            : acquittalData.varianceAmount > 0
                                                            ? alpha('#2196f3', 0.1)
                                                            : alpha(theme.palette.error.main, 0.1),
                                                    display: 'flex',
                                                    justifyContent: 'space-between',
                                                    alignItems: 'center'
                                                }}
                                            >
                                                <Typography
                                                    variant="subtitle2"
                                                    sx={{
                                                        fontWeight: 600,
                                                        color: (theme) =>
                                                            acquittalData.varianceAmount === 0
                                                                ? theme.palette.success.main
                                                                : acquittalData.varianceAmount > 0
                                                                ? '#2196f3'
                                                                : theme.palette.error.main
                                                    }}
                                                >
                                                    {acquittalData.varianceAmount > 0
                                                        ? 'TRAVELLER IS OWED'
                                                        : acquittalData.varianceAmount === 0
                                                        ? 'TRAVELLER IS BALANCED'
                                                        : 'TRAVELLER IS OWING'}
                                                </Typography>
                                                <Typography
                                                    variant="subtitle1"
                                                    sx={{
                                                        fontWeight: 700,
                                                        color: (theme) =>
                                                            acquittalData.varianceAmount === 0
                                                                ? theme.palette.success.main
                                                                : acquittalData.varianceAmount > 0
                                                                ? '#2196f3'
                                                                : theme.palette.error.main
                                                    }}
                                                >
                                                    {formatCurrency(Math.abs(acquittalData.varianceAmount))}
                                                </Typography>
                                            </Box>
                                        </>
                                    )}
                                </Stack>
                            </CardContent>
                        </Card>
                    )}
                </Stack>
            ) : (
                <TableContainer>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>Date</TableCell>
                                <TableCell align="right">Meals</TableCell>
                                <TableCell align="right">Accommodation</TableCell>
                                <TableCell align="right">Additional</TableCell>
                                <TableCell align="right">Total</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {lineActivities.map((activity) => {
                                const meals = (activity.breakfast || 0) + (activity.lunch || 0) + (activity.dinner || 0);
                                const additional = (activity.incidental || 0) + (activity.miscellaneous || 0);
                                const rowTotal = meals + (activity.accommodation || 0) + additional;

                                return (
                                    <TableRow key={activity.id || activity.activityDate}>
                                        <TableCell>{new Date(activity.activityDate).toLocaleDateString()}</TableCell>
                                        <TableCell align="right">{formatCurrency(meals)}</TableCell>
                                        <TableCell align="right">{formatCurrency(activity.accommodation || 0)}</TableCell>
                                        <TableCell align="right">{formatCurrency(additional)}</TableCell>
                                        <TableCell align="right">{formatCurrency(rowTotal)}</TableCell>
                                    </TableRow>
                                );
                            })}

                            {/* Summary Row */}
                            {lineActivities.length > 0 && (
                                <>
                                    <TableRow
                                        sx={{
                                            bgcolor: theme.primary.lighter,
                                            '& > td': {
                                                borderTop: `2px solid ${theme.primary.main}`,
                                                py: 2,
                                                typography: 'subtitle1',
                                                fontWeight: 600
                                            }
                                        }}
                                    >
                                        <TableCell>
                                            <Typography variant="subtitle1" sx={{ fontWeight: 600, color: theme.primary.dark }}>
                                                TOTAL
                                            </Typography>
                                        </TableCell>
                                        <TableCell align="right" sx={{ color: theme.primary.dark }}>
                                            {formatCurrency(calculateLineActivityTotals(lineActivities).meals)}
                                        </TableCell>
                                        <TableCell align="right" sx={{ color: theme.primary.dark }}>
                                            {formatCurrency(calculateLineActivityTotals(lineActivities).accommodation)}
                                        </TableCell>
                                        <TableCell align="right" sx={{ color: theme.primary.dark }}>
                                            {formatCurrency(calculateLineActivityTotals(lineActivities).additional)}
                                        </TableCell>
                                        <TableCell align="right">
                                            <Typography
                                                variant="subtitle1"
                                                sx={{
                                                    fontWeight: 700,
                                                    color: theme.primary.dark,
                                                    fontSize: '1.1rem'
                                                }}
                                            >
                                                {formatCurrency(calculateLineActivityTotals(lineActivities).total)}
                                            </Typography>
                                        </TableCell>
                                    </TableRow>

                                    {/* Total Spent Row */}
                                    {acquittalData && (
                                        <TableRow
                                            sx={{
                                                bgcolor: '#E8F5E9',
                                                '& > td': {
                                                    py: 2,
                                                    typography: 'subtitle1',
                                                    fontWeight: 600
                                                }
                                            }}
                                        >
                                            <TableCell colSpan={4}>
                                                <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#2E7D32' }}>
                                                    TOTAL SPENT
                                                </Typography>
                                            </TableCell>
                                            <TableCell align="right">
                                                <Typography
                                                    variant="subtitle1"
                                                    sx={{
                                                        fontWeight: 700,
                                                        color: '#2E7D32',
                                                        fontSize: '1.1rem'
                                                    }}
                                                >
                                                    {formatCurrency(acquittalData.totalSpentAmount)}
                                                </Typography>
                                            </TableCell>
                                        </TableRow>
                                    )}

                                    {acquittalData && (
                                        <TableRow
                                            sx={{
                                                bgcolor: '#E8F5E9',
                                                '& > td': {
                                                    py: 2,
                                                    typography: 'subtitle1',
                                                    fontWeight: 600
                                                }
                                            }}
                                        >
                                            <TableCell colSpan={4}>
                                                <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#2E7D32' }}>
                                                    TOTAL AMOUNT RECEIVED
                                                </Typography>
                                            </TableCell>
                                            <TableCell align="right">
                                                <Typography
                                                    variant="subtitle1"
                                                    sx={{
                                                        fontWeight: 700,
                                                        color: '#2E7D32',
                                                        fontSize: '1.1rem'
                                                    }}
                                                >
                                                    {formatCurrency(acquittalData.clientReceivedAmount)}
                                                </Typography>
                                            </TableCell>
                                        </TableRow>
                                    )}

                                    {/* Variance Row */}
                                    {acquittalData && (
                                        <TableRow
                                            sx={{
                                                bgcolor: (theme) =>
                                                    acquittalData.varianceAmount === 0
                                                        ? alpha(theme.palette.success.main, 0.1) // Light green for zero
                                                        : acquittalData.varianceAmount > 0
                                                        ? alpha('#2196f3', 0.1) // Light blue when owed (positive variance)
                                                        : alpha(theme.palette.error.main, 0.1), // Light red when owing (negative variance)
                                                '& > td': {
                                                    py: 2,
                                                    typography: 'subtitle1',
                                                    fontWeight: 600,
                                                    borderBottom: 'none'
                                                }
                                            }}
                                        >
                                            <TableCell colSpan={4}>
                                                <Typography
                                                    variant="subtitle1"
                                                    sx={{
                                                        fontWeight: 600,
                                                        color: (theme) =>
                                                            acquittalData.varianceAmount === 0
                                                                ? theme.palette.success.main // Green for zero
                                                                : acquittalData.varianceAmount > 0
                                                                ? '#2196f3' // Blue when owed (positive variance)
                                                                : theme.palette.error.main // Red when owing (negative variance)
                                                    }}
                                                >
                                                    {acquittalData.varianceAmount > 0
                                                        ? 'TRAVELLER IS OWED' // When received > spent (positive variance)
                                                        : acquittalData.varianceAmount === 0
                                                        ? 'TRAVELLER IS BALANCED'
                                                        : 'TRAVELLER IS OWING'}{' '}
                                                </Typography>
                                            </TableCell>
                                            <TableCell align="right">
                                                <Typography
                                                    variant="subtitle1"
                                                    sx={{
                                                        fontWeight: 700,
                                                        color: (theme) =>
                                                            acquittalData.varianceAmount === 0
                                                                ? theme.palette.success.main // Green for zero
                                                                : acquittalData.varianceAmount > 0
                                                                ? '#2196f3' // Blue when owed (positive variance)
                                                                : theme.palette.error.main, // Red when owing (negative variance)
                                                        fontSize: '1.1rem'
                                                    }}
                                                >
                                                    {formatCurrency(Math.abs(acquittalData.varianceAmount))}
                                                </Typography>
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}
        </Box>
    );
};

AcquittalLineActivitiesTable.propTypes = {
    lineActivities: PropTypes.arrayOf(
        PropTypes.shape({
            id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
            activityDate: PropTypes.string.isRequired,
            breakfast: PropTypes.number,
            lunch: PropTypes.number,
            dinner: PropTypes.number,
            accommodation: PropTypes.number,
            incidental: PropTypes.number,
            miscellaneous: PropTypes.number
        })
    ),
    title: PropTypes.string,
    formatCurrency: PropTypes.func,
    acquittalData: PropTypes.shape({
        totalSpentAmount: PropTypes.number,
        varianceAmount: PropTypes.number
    })
};

export default AcquittalLineActivitiesTable;
